/**
 * Link an authored row to a Cyber Academy course.
 *
 * Both tiers that carry a story need this - a lab and a scenario - and it is the one field on
 * either form that cannot be typed, because the value is an id in somebody else's system. Two
 * copies of a hundred-line picker is two places for the Moodle instance to drift, and picking the
 * wrong instance is not a visible mistake: the customer-facing Moodle has courses with the same
 * shape and entirely different ids.
 *
 * Only the id is stored, with the name beside it as a snapshot for reading. A course renamed in
 * Moodle therefore does not detach anything that points at it, and a stale name here is a display
 * problem rather than a broken link.
 */

import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import { useMoodleCourses } from '../../hooks/useMoodleHooks';
import { LabLoading, LabStatusTag, labErrorMessage } from './LabShared';

export type LabCoursePickerProps = {
  courseUid?: number | null;
  courseName?: string;
  /** Both halves at once: the id is authoritative and the name is the snapshot beside it. */
  onChange: (course: { course_uid: number | null; course_name: string }) => void;
  /**
   * Whether the form is on screen at all. The course list is a live Moodle call, so it is not made
   * until the picker is both mounted and open.
   */
  active?: boolean;
  /** What is missing without a course, in this tier's words. */
  unlinkedWarning?: string;
  className?: string;
};

const LabCoursePicker: React.FC<LabCoursePickerProps> = ({
  courseUid,
  courseName,
  onChange,
  active = true,
  unlinkedWarning = 'No course, so nothing tells the learner what any of this configuration was for. It still runs.',
  className = '',
}) => {
  // Opens on its own when there is nothing linked, since that is the case the row is incomplete
  // in, and closed when there is - it is a long list to scroll past to reach the rest of the form.
  const [open, setOpen] = useState(!courseUid);
  const [search, setSearch] = useState('');

  /**
   * `internal` is the Cyber Academy Moodle. The default instance is the customer-facing one, whose
   * course ids mean something else entirely - so the instance is named here rather than defaulted.
   */
  const { data: courses, isLoading, error } = useMoodleCourses(
    { enabled: active && open },
    'internal',
  );

  /** Filtered in the browser: Cyber Academy is one Moodle and the whole list is one call. */
  const matching = useMemo(() => {
    const all = courses || [];
    const needle = search.trim().toLowerCase();

    if (!needle) return all.slice(0, 50);

    return all
      .filter((course) =>
        `${course.fullname || ''} ${course.shortname || ''}`.toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [courses, search]);

  return (
    <div className={`rounded-md border border-gray-200 p-3 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-700">Cyber Academy course</span>
        <div className="flex flex-wrap items-center gap-2">
          {courseUid ? (
            <LabStatusTag label={`course ${courseUid}`} sentiment="success" />
          ) : (
            <LabStatusTag label="Not linked" sentiment="warning" />
          )}
          <StbButton role="default" size="xsmall" onClick={async () => setOpen(!open)}>
            {open ? 'Done' : courseUid ? 'Change' : 'Find the course'}
          </StbButton>
        </div>
      </div>

      {courseUid ? (
        <p className="m-0 mt-2 text-sm">
          {courseName || `Course ${courseUid}`}
          <span className="ml-2 text-xs text-gray-500">
            The name is a snapshot for reading; the id is what is authoritative, so renaming the
            course in Moodle does not detach this.
          </span>
        </p>
      ) : (
        <p className="m-0 mt-2 text-xs text-amber-800">{unlinkedWarning}</p>
      )}

      {open && (
        <div className="mt-3">
          <TextField
            label="Search Cyber Academy"
            value={search}
            onChange={(evt) => setSearch(evt.target.value)}
            helperText="By course name or short name."
            size="small"
            fullWidth
          />

          {isLoading ? (
            <LabLoading label="Loading Cyber Academy courses..." />
          ) : error ? (
            // Everything else on the form still saves, and saying so stops an unreachable Moodle
            // reading as a broken form.
            <p className="m-0 mt-2 text-xs text-amber-800">
              {labErrorMessage(
                error,
                'Cyber Academy could not be reached, so the course cannot be looked up right now. Everything else on this form still saves.',
              )}
            </p>
          ) : !matching.length ? (
            <p className="m-0 mt-2 text-xs text-gray-500">
              {search.trim() ? 'No course matches that.' : 'Cyber Academy returned no courses.'}
            </p>
          ) : (
            <div className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-100">
              {matching.map((course) => (
                <button
                  key={course.id}
                  type="button"
                  className={`block w-full border-b border-gray-100 px-2 py-1.5 text-left text-sm last:border-b-0 hover:bg-gray-50 ${
                    courseUid === course.id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => {
                    onChange({ course_uid: course.id, course_name: course.fullname || '' });
                    setOpen(false);
                  }}
                >
                  <span className="font-medium">{course.fullname}</span>
                  <span className="block text-xs text-gray-500">
                    {course.shortname} • course {course.id}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Only 50 are rendered, and saying so beats a list that silently stops. */}
          {matching.length === 50 && (
            <p className="m-0 mt-1 text-xs text-gray-500">
              Showing the first 50. Narrow the search to see the rest.
            </p>
          )}

          {!!courseUid && (
            <div className="mt-2">
              <StbButton
                role="default"
                size="xsmall"
                onClick={async () => onChange({ course_uid: null, course_name: '' })}
              >
                Unlink the course
              </StbButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LabCoursePicker;
